import { GLOBAL_API } from "../actionTypes/types";

// ─────────────────────────────────────────────────────────────────────────────
// ESHS LMS — learner (public/auth) configuration.
//
// Tenancy is PATH-BASED: the learner app is served on the shared app host and
// every learner URL is `/e-learning/{lmsShortName}/{child}`. The short name
// in the path selects the tenant and is sent on every public call; there is no
// per-tenant subdomain. (The subdomain scheme was removed — see resolveLmsShortName.)
//
// Base URL: the spec's `${VITE_API_BASE}/nodeapi/ec-card-gateway/api/lms/v1`
// maps here to `${GLOBAL_API}/lms/v1` — GLOBAL_API already carries the
// `/nodeapi/ec-card-gateway/api` prefix. Learner public endpoints live under
// `/lms/v1/public/*`; authenticated ones under `/lms/v1/*`.
// ─────────────────────────────────────────────────────────────────────────────

// Learner API base. Path-based tenancy means the portal and the API share one
// host, so the configured GLOBAL_API is used as-is (no per-host rewriting).
export const LMS_API = `${String(GLOBAL_API || "").replace(/\/$/, "")}/lms/v1`;

// ─────────────────────────────────────────────────────────────────────────────
// Learner portal browser routes.
// Every learner URL is `/e-learning/{lmsShortName}/{child}`. The short name is
// carried in the path (path-based tenancy), and `{child}` is the page (login,
// register, dashboard, course/…, …).
// ─────────────────────────────────────────────────────────────────────────────
export const LEARNER_ROUTE_PREFIX = "e-learning";

// Build a learner portal path. `learnerPath("crowd")` -> "/e-learning/crowd";
// `learnerPath("crowd", "dashboard")` -> "/e-learning/crowd/dashboard".
export const learnerPath = (shortName, child = "") => {
  const base = `/${LEARNER_ROUTE_PREFIX}/${cleanShortName(shortName)}`;
  const sub = String(child || "").replace(/^\/+/, "");
  return sub ? `${base}/${sub}` : base;
};

// Convert a legacy URL mounted below the retired `/eshs` segment to its
// canonical learner path without losing activation/reset query parameters.
export const legacyLearnerRedirectPath = (
  shortName,
  child = "login",
  search = "",
  hash = ""
) => `${learnerPath(shortName, child || "login")}${search || ""}${hash || ""}`;

// Match the short name out of the learner path, e.g.
// `/e-learning/crowd/register` -> "crowd".
export const LEARNER_PATH_RE = new RegExp(
  `^/${LEARNER_ROUTE_PREFIX}/([^/]+)(?:/|$)`,
  "i"
);

// The learner-frontend shared host (the spec's VITE_LEARNER_DOMAIN; CRA channel
// is REACT_APP_*). The tenant is selected by the URL path, not a subdomain. Used
// by the super-admin organization form's short-name preview/link.
const runtimeHost = () =>
  typeof window === "undefined" ? "" : window.location.hostname.toLowerCase();

export const LEARNER_DOMAIN =
  process.env.REACT_APP_LEARNER_DOMAIN ||
  (runtimeHost() === "localhost" || runtimeHost().endsWith(".localhost")
    ? "localhost"
    : "learningdomain.com");

const learnerDomainHost = () =>
  String(LEARNER_DOMAIN || "")
    .toLowerCase()
    .replace(/^\./, "")
    .split(":")[0];

const cleanShortName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");

// Registration is tenant-selected by short name. Resolve it from explicit
// runtime/build configuration first, then a query value, and finally the
// `/e-learning/{shortName}/…` path. Never infer the tenant from the
// learner's email or the hostname (path-based tenancy: no subdomain).
export const resolveLmsShortName = () => {
  if (typeof window === "undefined") {
    return cleanShortName(process.env.REACT_APP_LMS_SHORT_NAME);
  }

  const configured =
    window.__LMS_SHORT_NAME__ || process.env.REACT_APP_LMS_SHORT_NAME;
  if (configured) return cleanShortName(configured);

  const queryValue = new URLSearchParams(window.location.search).get(
    "lmsShortName"
  );
  if (queryValue) return cleanShortName(queryValue);

  const routeMatch = window.location.pathname.match(LEARNER_PATH_RE);
  if (routeMatch?.[1]) return cleanShortName(routeMatch[1]);

  return "";
};

// True when the current URL is a learner portal path (path-based tenancy;
// replaces the old subdomain host check).
export const isLearnerPortalPath = () => {
  if (typeof window === "undefined") return false;
  return LEARNER_PATH_RE.test(window.location.pathname);
};

// Absolute, shareable learner portal URL for a tenant, built on the learner
// apex host (no subdomain) with the path carrying the short name. `path`
// defaults to the tenant's registration page.
export const learnerPortalUrl = (shortName, path) => {
  const clean = cleanShortName(shortName);
  if (!clean) return "";
  const protocol =
    typeof window !== "undefined" ? window.location.protocol : "https:";
  const port = typeof window !== "undefined" ? window.location.port : "";
  const domain = learnerDomainHost();
  const pathname = path
    ? path.startsWith("/")
      ? path
      : `/${path}`
    : learnerPath(clean, "register");
  return `${protocol}//${domain}${port ? `:${port}` : ""}${pathname}`;
};

// Fallback org used for local development when no runtime tenant mapping exists.
// (CrowdApps Demo Organisation on the current dev DB.)
const DEFAULT_ORG_ID = "KWE--CLm79mc-KGZqkrFQ";

// Legacy secondary tenant hint. The public auth APIs send `lmsShortName` as the
// authoritative tenant selector (the server prefers it); `orgId` is only a
// fallback the server uses when no short name is supplied. Kept for that
// contract. Precedence: runtime-injected window value → build env → dev default.
export const resolveOrgId = () => {
  if (typeof window !== "undefined" && window.__LMS_ORG_ID__) {
    return window.__LMS_ORG_ID__;
  }
  return process.env.REACT_APP_LMS_ORG_ID || DEFAULT_ORG_ID;
};

// Hint the client sends for activation / reset email links. NOTE: the backend is
// authoritative — it rebuilds this from the org's short name (path-based, no
// subdomain) and ignores the client value — so this is only a sensible default.
export const setPasswordBaseUrl = () =>
  `${window.location.origin}${learnerPath(resolveLmsShortName(), "set-password")}`;

export const resetBaseUrl = setPasswordBaseUrl;

// Client-mirror of the learner password policy.
export const PASSWORD_MIN = 8;
export const passwordPolicyOk = (pw = "") =>
  pw.length >= PASSWORD_MIN &&
  /[A-Z]/.test(pw) &&
  /[a-z]/.test(pw) &&
  /[0-9]/.test(pw) &&
  /[^A-Za-z0-9]/.test(pw);

// Basic email shape check (server is authoritative; this is only for inline UX).
export const emailShapeOk = (email = "") =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
